import { useState } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/app/components/ui/dialog';
import { Button } from '@/app/components/ui/button';
import { Wallet, LogOut } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { tokenUtils } from '@/api/axios.config';

export function WalletButton() {
  const { login, logout, authenticated, user, ready } = usePrivy();
  const { clearUser } = useAuthStore();
  const [showModal, setShowModal] = useState(false);

  const handleConnect = () => {
    login();
  };

  const handleDisconnect = () => {
    logout();
    tokenUtils.removeToken();
    clearUser();
    setShowModal(false);
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 5)}...${address.slice(-5)}`;
  };

  // Get Solana wallet from linked accounts
  const solanaLinkedAccount = user?.linkedAccounts?.find(
    (acc: { type?: string; chainType?: string; address?: string }) =>
      acc.type === 'wallet' && acc.chainType === 'solana' && acc.address
  );
  const walletAddress = solanaLinkedAccount && 'address' in solanaLinkedAccount
    ? solanaLinkedAccount.address
    : null;

  if (!ready) {
    return (
      <button
        disabled
        className="bg-white rounded-[10px] h-[36px] px-[16px] flex items-center gap-[8px] opacity-50 flex-1 min-w-[157px]"
      >
        <Wallet className="w-[16px] h-[16px] text-black" />
        <span className="font-['Inter'] font-medium text-[14px] leading-[20px] text-black text-center flex-1">
          Loading...
        </span>
      </button>
    );
  }

  if (authenticated) {
    const address = walletAddress ? formatAddress(walletAddress) : 'Connected';

    return (
      <>
        <button
          onClick={() => setShowModal(true)}
          className="bg-white rounded-[10px] h-[36px] px-[16px] flex items-center gap-[8px] hover:opacity-90 transition-opacity flex-1 min-w-[157px]"
        >
          <Wallet className="w-[16px] h-[16px] text-black" />
          <span className="font-['Inter'] font-medium text-[14px] leading-[20px] text-black text-center flex-1">
            {address}
          </span>
        </button>

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[#18181f] border-white/[0.08] text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Poppins',sans-serif] text-xl">
                Wallet Connected
              </DialogTitle>
              <DialogDescription className="text-[#9595a5]">
                Your wallet is connected and ready to use.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-[#2a2a35]/50 border border-white/[0.08]">
                <div className="text-xs text-[#9595a5] mb-1">Address</div>
                <div className="text-sm font-mono text-white break-all">{walletAddress || 'Wallet connected'}</div>
              </div>
              <Button
                onClick={handleDisconnect}
                variant="outline"
                className="w-full bg-transparent border-white/[0.08] text-white hover:bg-[#2a2a35]/50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <button
      onClick={handleConnect}
      className="bg-white rounded-[10px] h-[36px] px-[16px] flex items-center gap-[8px] hover:opacity-90 transition-opacity flex-1 min-w-[157px]"
    >
      <Wallet className="w-[16px] h-[16px] text-black" />
      <span className="font-['Inter'] font-medium text-[14px] leading-[20px] text-black text-center flex-1">
        Connect Wallet
      </span>
    </button>
  );
}
